import { exportDocxXml } from './docxXmlParser'
/**
 * ExportManager - Multi-format export for CAT projects
 * 
 * Supported formats:
 * - XLIFF (.xlf) - Standard translation format
 * - DOCX (.docx) - Microsoft Word (bilingual or target only)
 * - PDF (.pdf) - Portable Document Format (bilingual or target only)
 * - TXT (.txt) - Plain text (target only)
 * - TMX (.tmx) - Translation Memory eXchange (from completed segments)
 * 
 * Dependencies: docx, jspdf
 */

import { Document, Paragraph, TextRun, Table, TableCell, TableRow, WidthType } from 'docx'
import { jsPDF } from 'jspdf'

interface CATSegment {
  id: string
  source_text: string
  source_tagged_text?: string | null
  source_tags?: Array<{
    id: string
    type: 'bold' | 'italic' | 'underline' | 'link' | 'footnote' | 'style'
    position: [number, number]
    content?: string
    attributes?: Record<string, string>
  }>
  target_text: string
  target_tagged_text?: string | null
  xml_paragraph_index?: number | null
  segment_order: number
  status: 'new' | 'in_progress' | 'completed' | 'approved' | 'confirmed' | 'rejected'
  locked: boolean
  notes: string
}

interface CATProject {
  id: string
  name: string
  source_language: string
  target_language: string
  total_segments: number
  completed_segments: number
  word_count: number
  file_url?: string | null
}

export type ExportFormat = 'xliff' | 'docx-bilingual' | 'docx-target' | 'pdf-bilingual' | 'pdf-target' | 'txt' | 'tmx'

export class ExportManager {
  /**
   * Export project to specified format
   */
  static async exportProject(
    project: CATProject,
    segments: CATSegment[],
    format: ExportFormat
  ): Promise<{ blob: Blob; filename: string }> {
    console.log(`📤 Exporting project as ${format}...`)

    switch (format) {
      case 'xliff':
        return this.exportXLIFF(project, segments)
      
      case 'docx-bilingual':
        return this.exportDOCXBilingual(project, segments)
      
      case 'docx-target':
        return this.exportDOCXTarget(project, segments)
      
      case 'pdf-bilingual':
        return this.exportPDFBilingual(project, segments)
      
      case 'pdf-target':
        return this.exportPDFTarget(project, segments)
      
      case 'txt':
        return this.exportTXT(project, segments)
      
      case 'tmx':
        return this.exportTMX(project, segments)
      
      default:
        throw new Error(`Unsupported format: ${format}`)
    }
  }

  /**
   * Export as XLIFF
   */
  private static exportXLIFF(project: CATProject, segments: CATSegment[]): { blob: Blob; filename: string } {
    const xliffHeader = `<?xml version="1.0" encoding="UTF-8"?>
<xliff version="1.2">
  <file source-language="${project.source_language}" target-language="${project.target_language}" datatype="plaintext" original="${project.name}">
    <body>`

    const xliffSegments = segments.map(segment => `
      <trans-unit id="${segment.segment_order}" approved="${segment.status === 'approved' ? 'yes' : 'no'}">
        <source>${this.escapeXml(segment.source_text || '')}</source>
        <target state="${segment.status}">${this.escapeXml(segment.target_text || '')}</target>
        ${segment.notes ? `<note>${this.escapeXml(segment.notes)}</note>` : ''}
      </trans-unit>`).join('')

    const xliffFooter = `
    </body>
  </file>
</xliff>`

    const content = xliffHeader + xliffSegments + xliffFooter
    const blob = new Blob([content], { type: 'application/xml' })
    const filename = `${this.sanitizeFilename(project.name)}.xlf`

    return { blob, filename }
  }

  /**
   * Export as DOCX (Bilingual table)
   */
  private static async exportDOCXBilingual(project: CATProject, segments: CATSegment[]): Promise<{ blob: Blob; filename: string }> {
    const doc = new Document({
      sections: [{
        properties: {},
        children: [
          // Title
          new Paragraph({
            text: project.name,
            heading: 'Heading1'
          }),
          
          // Info
          new Paragraph({
            text: `${this.formatLanguage(project.source_language)} → ${this.formatLanguage(project.target_language)}`,
            spacing: { after: 200 }
          }),
          
          // Bilingual table
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows: [
              // Header row
              new TableRow({
                children: [
                  new TableCell({
                    children: [new Paragraph({ text: '#', bold: true })],
                    width: { size: 5, type: WidthType.PERCENTAGE }
                  }),
                  new TableCell({
                    children: [new Paragraph({ 
                      text: `Source (${this.formatLanguage(project.source_language)})`,
                      bold: true 
                    })],
                    width: { size: 47.5, type: WidthType.PERCENTAGE }
                  }),
                  new TableCell({
                    children: [new Paragraph({ 
                      text: `Translation (${this.formatLanguage(project.target_language)})`,
                      bold: true 
                    })],
                    width: { size: 47.5, type: WidthType.PERCENTAGE }
                  })
                ]
              }),
              
              // Segment rows
              ...segments.map(segment => new TableRow({
                children: [
                  new TableCell({
                    children: [new Paragraph(segment.segment_order.toString())]
                  }),
                  new TableCell({
                    children: [new Paragraph(segment.source_text || '')]
                  }),
                  new TableCell({
                    children: [new Paragraph(segment.target_text || '')]
                  })
                ]
              }))
            ]
          })
        ]
      }]
    })

    // Generate blob (requires Packer from docx)
    const { Packer } = await import('docx')
    const buffer = await Packer.toBlob(doc)
    const filename = `${this.sanitizeFilename(project.name)}_bilingual.docx`

    return { blob: buffer, filename }
  }

  /**
   * Build formatted TextRun array from target text + source tags
   * Applies source formatting positions to target text
   */
  /**
   * Parse HTML string into TextRun array
   * Handles <b>, <strong>, <i>, <em>, <u>, <a> tags
   */
  private static htmlToTextRuns(html: string): TextRun[] {
    const runs: TextRun[] = []
    const parser = new DOMParser()
    const doc = parser.parseFromString(`<span>${html}</span>`, 'text/html')
    const root = doc.querySelector('span')
    if (!root) return [new TextRun({ text: html.replace(/<[^>]+>/g, '') })]

    const processNode = (node: Node, bold = false, italics = false, underline = false) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || ''
        if (text) {
          runs.push(new TextRun({
            text,
            bold: bold || undefined,
            italics: italics || undefined,
            underline: underline ? {} : undefined,
          }))
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as Element
        const tag = el.tagName.toLowerCase()
        const isBold = bold || tag === 'b' || tag === 'strong'
        const isItalic = italics || tag === 'i' || tag === 'em'
        const isUnderline = underline || tag === 'u'
        node.childNodes.forEach(child => processNode(child, isBold, isItalic, isUnderline))
      }
    }

    root.childNodes.forEach(node => processNode(node))
    return runs.length > 0 ? runs : [new TextRun({ text: root.textContent || '' })]
  }

  private static buildFormattedRuns(segment: CATSegment): TextRun[] {
    // Priority 1: use target_tagged_text (from HTML-aware MT)
    if (segment.target_tagged_text) {
      return this.htmlToTextRuns(segment.target_tagged_text)
    }
    // Priority 2: use source_tagged_text with target plain text (positional mapping)
    if (segment.source_tagged_text && segment.target_text) {
      // Replace source text content with target text, keep tags
      return this.htmlToTextRuns(segment.source_tagged_text.replace(
        /(<[^>]+>)|([^<]+)/g,
        (match, tag, text) => tag ? tag : segment.target_text || text
      ))
    }
    // Priority 3: plain text, no formatting
    return [new TextRun({ text: segment.target_text || '' })]
  }

  /**
   * Export as DOCX (Target only) — XML surgery when original file available
   */
  private static async exportDOCXTarget(project: CATProject, segments: CATSegment[]): Promise<{ blob: Blob; filename: string }> {
    const filename = `${this.sanitizeFilename(project.name)}_${project.target_language}.docx`

    // ✅ XML SURGERY — inject translations into original DOCX (preserves everything)
    if (project.file_url) {
      try {
        // Build paragraph index → translation map
        const translations: Record<number, string> = {}
        for (const seg of segments) {
          if (seg.xml_paragraph_index != null && seg.target_text?.trim()) {
            translations[seg.xml_paragraph_index] = seg.target_text
          }
        }
        const blob = await exportDocxXml(project.file_url, translations)
        console.log('✅ XML surgery export complete')
        return { blob, filename }
      } catch (err) {
        console.warn('XML surgery failed, falling back to rebuild:', err)
      }
    }

    // Fallback: rebuild from scratch (loses formatting)
    const doc = new Document({
      sections: [{
        properties: {},
        children: [
          new Paragraph({ text: project.name, heading: 'Heading1' }),
          new Paragraph({
            text: `Translation: ${this.formatLanguage(project.target_language)}`,
            spacing: { after: 200 }
          }),
          ...segments.map(segment =>
            new Paragraph({
              children: this.buildFormattedRuns(segment),
              spacing: { after: 100 }
            })
          )
        ]
      }]
    })
    const { Packer } = await import('docx')
    const buffer = await Packer.toBlob(doc)
    return { blob: buffer, filename }
  }

  /**
   * Export as PDF (Bilingual)
   */
  private static exportPDFBilingual(project: CATProject, segments: CATSegment[]): { blob: Blob; filename: string } {
    const doc = new jsPDF()
    
    // Title
    doc.setFontSize(18)
    doc.text(project.name, 15, 20)
    
    // Language info
    doc.setFontSize(12)
    doc.text(
      `${this.formatLanguage(project.source_language)} → ${this.formatLanguage(project.target_language)}`,
      15,
      30
    )
    
    let y = 45
    const lineHeight = 7
    const pageHeight = doc.internal.pageSize.height
    const margin = 15
    
    segments.forEach((segment, index) => {
      // Check if we need a new page
      if (y > pageHeight - 30) {
        doc.addPage()
        y = 20
      }
      
      // Segment number
      doc.setFontSize(10)
      doc.setTextColor(100, 100, 100)
      doc.text(`#${segment.segment_order}`, margin, y)
      y += lineHeight
      
      // Source text
      doc.setFontSize(11)
      doc.setTextColor(0, 0, 0)
      const sourceLines = doc.splitTextToSize(
        `Source: ${segment.source_text || ''}`,
        doc.internal.pageSize.width - 2 * margin
      )
      doc.text(sourceLines, margin, y)
      y += sourceLines.length * lineHeight
      
      // Target text
      const targetLines = doc.splitTextToSize(
        `Translation: ${segment.target_text || ''}`,
        doc.internal.pageSize.width - 2 * margin
      )
      doc.text(targetLines, margin, y)
      y += targetLines.length * lineHeight + 5
    })
    
    const blob = doc.output('blob')
    const filename = `${this.sanitizeFilename(project.name)}_bilingual.pdf`
    
    return { blob, filename }
  }

  /**
   * Export as PDF (Target only)
   */
  private static exportPDFTarget(project: CATProject, segments: CATSegment[]): { blob: Blob; filename: string } {
    const doc = new jsPDF()
    
    // Title
    doc.setFontSize(18)
    doc.text(project.name, 15, 20)
    
    // Language info
    doc.setFontSize(12)
    doc.text(`Translation: ${this.formatLanguage(project.target_language)}`, 15, 30)
    
    let y = 45
    const lineHeight = 7
    const pageHeight = doc.internal.pageSize.height
    const margin = 15
    
    segments.forEach(segment => {
      if (y > pageHeight - 20) {
        doc.addPage()
        y = 20
      }
      
      doc.setFontSize(11)
      const lines = doc.splitTextToSize(
        segment.target_text || '',
        doc.internal.pageSize.width - 2 * margin
      )
      doc.text(lines, margin, y)
      y += lines.length * lineHeight + 3
    })
    
    const blob = doc.output('blob')
    const filename = `${this.sanitizeFilename(project.name)}_${project.target_language}.pdf`
    
    return { blob, filename }
  }

  /**
   * Export as TXT (Target only)
   */
  private static exportTXT(project: CATProject, segments: CATSegment[]): { blob: Blob; filename: string } {
    const content = segments
      .map(segment => segment.target_text || '')
      .join('\n\n')
    
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
    const filename = `${this.sanitizeFilename(project.name)}_${project.target_language}.txt`
    
    return { blob, filename }
  }

  /**
   * Export as TMX (Translation Memory eXchange)
   * Only exports completed/approved segments
   */
  private static exportTMX(project: CATProject, segments: CATSegment[]): { blob: Blob; filename: string } {
    const completedSegments = segments.filter(s =>
      s.status === 'confirmed'
    )

    const tmxHeader = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE tmx SYSTEM "tmx14.dtd">
<tmx version="1.4">
  <header
    creationtool="TranslyxCAT"
    creationtoolversion="1.0"
    datatype="plaintext"
    segtype="sentence"
    adminlang="en-US"
    srclang="${project.source_language}"
    o-tmf="plaintext"
  />
  <body>`

    const tmxSegments = completedSegments.map(segment => `
    <tu>
      <tuv xml:lang="${project.source_language}">
        <seg>${this.escapeXml(segment.source_text || '')}</seg>
      </tuv>
      <tuv xml:lang="${project.target_language}">
        <seg>${this.escapeXml(segment.target_text || '')}</seg>
      </tuv>
    </tu>`).join('')

    const tmxFooter = `
  </body>
</tmx>`

    const content = tmxHeader + tmxSegments + tmxFooter
    const blob = new Blob([content], { type: 'application/xml' })
    const filename = `${this.sanitizeFilename(project.name)}_TM.tmx`

    return { blob, filename }
  }

  /**
   * Trigger browser download
   */
  static downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    
    console.log(`✅ Downloaded: ${filename}`)
  }

  /**
   * Escape XML special characters
   */
  private static escapeXml(unsafe: string): string {
    return unsafe
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
  }

  /**
   * Sanitize filename
   */
  private static sanitizeFilename(filename: string): string {
    return filename.replace(/[^a-zA-Z0-9_-]/g, '_')
  }

  /**
   * Format language code
   */
  private static formatLanguage(code: string): string {
    const langNames: Record<string, string> = {
      'en': 'English',
      'es': 'Spanish',
      'fr': 'French',
      'de': 'German',
      'it': 'Italian',
      'pt': 'Portuguese',
      'pt-br': 'Portuguese (BR)',
      'ru': 'Russian',
      'zh': 'Chinese',
      'ja': 'Japanese',
      'ar': 'Arabic'
    }
    return langNames[code.toLowerCase()] || code.toUpperCase()
  }
}